import { useCallback } from 'react';

import { cn } from '@lyearn/style';
import { Radio, Stack, Typography } from '@lyearn/ui';

import { textRadioButtonStyles } from '../styles';
import { RadioButtonAppearance, RadioButtonProps } from '../types';

const TextRadioButton = <T = string,>({
  isSelected,
  option,
  size,
  name,
  onChange,
  overrides,
  styleOverrides,
}: RadioButtonProps<T>) => {
  const { id, label, description, disabled: isDisabled } = option;
  const appearance: RadioButtonAppearance = description ? 'double' : 'single';
  const styles = textRadioButtonStyles[appearance][size];

  const onClick = useCallback(() => {
    if (isDisabled) return;
    onChange?.(id, option);
  }, [id, isDisabled, onChange, option]);

  return (
    <Stack
      alignItems={appearance === 'double' ? 'start' : 'center'}
      aria-checked={isSelected}
      direction="row"
      role="radio"
      onClick={onClick}
      {...(overrides?.container ?? {})}>
      <Radio
        checked={isSelected}
        className={cn(styleOverrides?.radio?.className ?? styles.radio.className, {
          'opacity-50': isDisabled,
        })}
        disabled={isDisabled}
        id={id as string}
        name={name}
        size={styleOverrides?.radio?.size ?? styles.radio.size}
      />
      <Stack direction="col" gapY={styles.gapY}>
        <Typography
          as="label"
          color="text-text-primary"
          // @ts-ignore it works
          for={id}
          numberOfLines={1}
          variant={styles.label.variant}
          {...(overrides?.labelProps ?? {})}>
          {label}
        </Typography>
        {styles.description?.variant ? (
          <Typography
            color="text-text-secondary"
            numberOfLines={1}
            variant={styles.description.variant}
            {...(overrides?.descriptionProps ?? {})}>
            {description}
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
};

export default TextRadioButton;
